/**
 * useNowPaymentsReturn.js — Détection du retour utilisateur après paiement NowPayments.
 *
 * Contexte (cart) : le user paye en crypto sur nowpayments.io (page externe), puis est
 * redirigé vers `/cart?nowpayments_order=cart_<token>_user_<id>_<ts>&cartOrderToken=<token>`.
 * Le webhook NowPayments arrive en parallèle (peut prendre quelques secondes après le retour
 * utilisateur). Ce hook polle l'endpoint serveur jusqu'à confirmation OU timeout.
 *
 * Source : porté depuis l'archive `_archive/2026-06-23-buyflow-legacy/Modals/nowPaymentsModal.jsx`
 * (lignes 31-32 : détection paramètre + appel confirmPayment). Ici on couvre les CartOrders
 * (`POST /cart/confirm-nowpayments-payment`) pas les Sale tiles uniques.
 *
 * Backend (server/src/controllers/cart.controller.js :: confirmCartNowPaymentsPayment) :
 *   - 200 + { success, alreadyConfirmed }                → user has nothing to wait, transfer done.
 *   - 200 + { success, alreadyProcessed, status }         → CartOrder déjà processé par webhook.
 *   - 200 + { success, cartOrderToken, completed, failed, status } → résultat normal.
 *   - 202 + { pending: true, message }                    → webhook pas encore arrivé → retry.
 *
 * §13 — Aucun verrou posé ici. §46 — Pas d'asset transféré ici (le backend décide après webhook).
 * §69 — Hook chirurgical, ne touche pas le flow normal Stripe/PayPal/Crypto.
 *
 * @param {Object}   handlers
 * @param {Function} handlers.onPending   (orderId) => void   — appelé une fois au montage si le param est détecté
 * @param {Function} handlers.onConfirmed (responseBody) => void — paiement confirmé serveur
 * @param {Function} handlers.onFailed    (error) => void     — erreur réseau ou 4xx/5xx
 * @param {Function} handlers.onTimeout   () => void          — épuisé MAX_RETRIES sans confirmation
 */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { openCart } from '../store/slices/cartSlice';
import { readStoredUser } from '../utils/auth/userStorage';
import * as selectors from '../store/selectors';
import { SERVER_URL as serverURL } from '../core/config';

const POLL_INTERVAL_MS = 3000;
const MAX_RETRIES = 10; // 10 × 3s ≈ 30s — au-delà, le webhook + cron prendront le relais

export function useNowPaymentsReturn({ onPending, onConfirmed, onFailed, onTimeout } = {}) {
  const dispatch = useDispatch();
  const signedUser = useSelector(selectors.user);
  // Refs pour éviter de redéclencher l'effect quand les handlers changent (parent re-render).
  const handlersRef = useRef({ onPending, onConfirmed, onFailed, onTimeout });
  handlersRef.current = { onPending, onConfirmed, onFailed, onTimeout };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('nowpayments_order');
    if (!orderId) return undefined;

    // §3 audit passe 2 — user délogué (mode privé, session expirée) : le poll
    // produirait un 401 et afficherait "FAILED" alors que le paiement est OK
    // côté serveur (webhook). On signale un état AUTH dédié, on ne clean PAS
    // l'URL : un re-login + F5 relance le polling sur l'orderId présent.
    //
    // Source du "signé" : Redux (hydraté par le Header au montage via setUser(localStorage._user))
    // OU repli immédiat sur localStorage._user. Le retour NowPayments est un redirect FULL-PAGE :
    // le hook peut fire AVANT que le Header n'ait dispatché setUser → signedUser Redux undefined
    // pour un user pourtant loggué → faux AUTH transitoire. localStorage._user est dispo dès le
    // boot et sert déjà l'auth axios du poll → on l'utilise comme source de vérité du "loggué".
    let effectiveUserId = signedUser?.id;
    if (!effectiveUserId) {
      effectiveUserId = readStoredUser()?.id;
    }
    if (!effectiveUserId) {
      // eslint-disable-next-line no-console
      console.warn('[useNowPaymentsReturn] user not signed — skipping poll');
      handlersRef.current.onFailed && handlersRef.current.onFailed({
        code: 'AUTH',
        message: 'Please sign in to view your crypto payment status.',
      });
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let timerId = null;

    const cleanUrl = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('nowpayments_order');
        url.searchParams.delete('cartOrderToken');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        // No-op : si replaceState n'est pas dispo (SSR, sandbox), on continue.
      }
    };

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await axiosWithToken(
          `${serverURL}/cart/confirm-nowpayments-payment`,
          'post',
          { orderId }
        );

        if (cancelled) return;

        // Cas pending — webhook NowPayments pas encore arrivé
        // (axios résout 202 comme succès → on lit le body décrypté).
        if (res && res.pending === true) {
          if (attempts < MAX_RETRIES) {
            timerId = setTimeout(poll, POLL_INTERVAL_MS);
          } else {
            handlersRef.current.onTimeout && handlersRef.current.onTimeout();
            // On clean l'URL : webhook + cron consume plus tard, l'user sera notifié.
            cleanUrl();
          }
          return;
        }

        // Succès — paiement confirmé serveur (transfer fait OU déjà traité).
        // §3 audit passe 2 — onConfirmed AVANT cleanUrl : si le callback throw,
        // l'orderId reste dans l'URL et un F5 relance le polling.
        handlersRef.current.onConfirmed && handlersRef.current.onConfirmed(res || {});
        cleanUrl();
      } catch (err) {
        if (cancelled) return;
        // §3 audit passe 2 — discriminer 4xx/5xx/AUTH pour que l'UI affiche un
        // message clair (vs "FAILED" générique → buyer contacte support à tort).
        const status = err?.response?.status;
        let payload;
        if (status === 401) {
          payload = { code: 'AUTH', message: 'Please sign in to view your crypto payment status.', status };
        } else if (status === 404) {
          payload = { code: 'NOT_FOUND', message: 'Transaction not found.', status };
        } else if (typeof status === 'number' && status >= 500) {
          payload = { code: 'SERVER_ERROR', message: 'Server error. Please try refreshing the page.', status };
        } else if (typeof status === 'number' && status >= 400) {
          payload = {
            code: 'CLIENT_ERROR',
            message: err?.response?.data?.message || 'There was an issue confirming this transaction.',
            status,
          };
        } else {
          // Réseau pur (pas de response) ou autre — on garde err pour debug.
          payload = { code: 'NETWORK', message: 'Network error. Please check your connection.', error: err };
        }
        handlersRef.current.onFailed && handlersRef.current.onFailed(payload);
        // Pour AUTH/NETWORK : on NE clean PAS l'URL → re-login + F5 relance.
        // Pour 404 (NOT_FOUND) ou 4xx/5xx définitifs : on clean.
        if (payload.code !== 'AUTH' && payload.code !== 'NETWORK') {
          cleanUrl();
        }
      }
    };

    // Annonce le retour pending (pour que le parent affiche le step Processing).
    handlersRef.current.onPending && handlersRef.current.onPending(orderId);
    // Ouvre le drawer panier en arrière-plan (cohérent avec le retour sur /cart).
    dispatch(openCart());
    // Démarre le polling immédiatement.
    poll();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, signedUser?.id]);
}

export default useNowPaymentsReturn;
